import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ShareIcon from '@mui/icons-material/Share';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Stack, 
  Typography, 
  Box, 
  Chip,
  IconButton,
  Divider,
  Dialog,
  DialogContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';
import NoMediafiles from '../../../assets/no-media.svg';

const BestPostCardDetails = ({
  title,
  mediaUrl,
  reactions = 0,
  shares = 0,
  comments = 0,
  createdTime,
  description,
  engagement,
  link,
  engagementRate,
  saved,
  reach,
}) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const [blobUrl, setBlobUrl] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    const fetchMediaAsBlob = async () => {
      if (mediaUrl) {
        try {
          const response = await fetch(mediaUrl);
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          return () => URL.revokeObjectURL(url);
        } catch (error) {
          console.error(`${translate('report.error_fetching_media')}:`, error);
        }
      }
    };

    fetchMediaAsBlob();
  }, [mediaUrl]);

  const handleImageClick = () => {
    if (blobUrl || mediaUrl) {
      setImageModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };

  return (
    <Card 
      sx={{ 
        maxWidth: 500, 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        overflow: 'hidden'
      }}
    >
      {/* Header Section */}
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.3,
                mb: 0.5
              }}
            >
              {title}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                {CommonHelper.DateTimeFromCurrentTime(createdTime)}
              </Typography>
              <Chip 
                label={`${engagementRate}%`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600
                }}
              />
            </Stack>
          </Box>
          <IconButton
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{
              color: 'primary.main',
              backgroundColor: 'primary.50',
              '&:hover': {
                backgroundColor: 'primary.100'
              }
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>

      {/* Description Section */}
      {description && (
        <CardContent sx={{ pt: 0, pb: 1 }}>
          <Typography
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              color: 'text.secondary',
              whiteSpace: 'pre-line'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      )}

      {/* Media Section */}
      <Box sx={{ position: 'relative', mx: 2, mb: 2 }}>
        <CardMedia
          component="img"
          image={blobUrl || mediaUrl || NoMediafiles}
          alt={title}
          onClick={handleImageClick}
          sx={{
            height: 180,
            borderRadius: '12px',
            objectFit: 'cover',
            backgroundColor: '#f5f5f5',
            cursor: (blobUrl || mediaUrl) && mediaUrl !== NoMediafiles ? 'pointer' : 'default',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              ...(blobUrl || mediaUrl) && mediaUrl !== NoMediafiles && {
                transform: 'scale(1.02)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }
            }
          }}
        />
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Stats Section */}
      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {/* First Row - Reactions and Comments */}
          <Stack direction="row" spacing={2}>
            <Box 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                height: '120px',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderRadius: '12px',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                minWidth: 0,
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
                <ThumbUpIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem'
                  }}
                >
                  {translate('report.reactions')}
                </Typography>
              </Stack>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  fontFamily: 'monospace',
                  letterSpacing: '-0.3px',
                  lineHeight: 1,
                  fontSize: '1.1rem'
                }}
              >
                {CommonHelper.formatNumber(reactions)}
              </Typography>
            </Box>

            <Box 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                height: '120px',
                backgroundColor: 'rgba(46, 125, 50, 0.04)',
                borderRadius: '12px',
                border: '1px solid rgba(46, 125, 50, 0.1)',
                minWidth: 0,
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(46, 125, 50, 0.08)',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)'
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
                <ChatBubbleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem'
                  }}
                >
                  {translate('report.comments')}
                </Typography>
              </Stack>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'success.main',
                  fontFamily: 'monospace',
                  letterSpacing: '-0.3px',
                  lineHeight: 1,
                  fontSize: '1.1rem'
                }}
              >
                {CommonHelper.formatNumber(comments)}
              </Typography>
            </Box>
          </Stack>

          {/* Second Row - Shares and Engagement */}
          <Stack direction="row" spacing={2}>
            <Box 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                height: '120px',
                backgroundColor: 'rgba(237, 108, 2, 0.04)',
                borderRadius: '12px',
                border: '1px solid rgba(237, 108, 2, 0.1)',
                minWidth: 0,
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(237, 108, 2, 0.08)',
                  border: '1px solid rgba(237, 108, 2, 0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(237, 108, 2, 0.15)'
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
                <ShareIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem'
                  }}
                >
                  {translate('report.shares')}
                </Typography>
              </Stack>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'warning.main',
                  fontFamily: 'monospace',
                  letterSpacing: '-0.3px',
                  lineHeight: 1,
                  fontSize: '1.1rem'
                }}
              >
                {CommonHelper.formatNumber(shares)}
              </Typography>
            </Box>

            <Box 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                height: '120px',
                backgroundColor: 'rgba(2, 136, 209, 0.04)',
                borderRadius: '12px',
                border: '1px solid rgba(2, 136, 209, 0.1)',
                minWidth: 0,
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(2, 136, 209, 0.08)',
                  border: '1px solid rgba(2, 136, 209, 0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(2, 136, 209, 0.15)'
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
                <TrendingUpIcon sx={{ color: 'info.main', fontSize: 16 }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem'
                  }}
                >
                  {translate('report.engagement')}
                </Typography>
              </Stack>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'info.main',
                  fontFamily: 'monospace',
                  letterSpacing: '-0.3px',
                  lineHeight: 1,
                  fontSize: '1.1rem'
                }}
              >
                {CommonHelper.formatNumber(engagement)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>

      {/* Full Size Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxHeight: '90vh',
            maxWidth: '90vw'
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: 'text.primary',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={blobUrl || mediaUrl}
            alt={title}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BestPostCardDetails;
